import React, { useState, useEffect } from 'react';
import { X, Search, FlaskConical, Clock, AlertCircle, ChevronDown, Gift, Stethoscope, Tag, Activity, DollarSign } from 'lucide-react';
import { API_URL } from '../config/api';
import './AssignStudyModal.css';

const AssignStudyModal = ({ patientId, patientName, onClose, onSuccess }) => {
    const [catalogStudies, setCatalogStudies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [doctors, setDoctors] = useState([]);
    const [requestingDoctor, setRequestingDoctor] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        category: '',
        processing_type: ''
    });

    const [selectedStudy, setSelectedStudy] = useState(null);
    const [formData, setFormData] = useState({
        priority: 'rutina',
        special_notes: '',
        is_cost_exempt: false
    });
    const getDoctorDisplayName = (doctor) => {
        if (!doctor) return 'SIN MEDICO';
        const fullName = [doctor.first_name, doctor.last_name, doctor.maternal_last_name]
            .filter(Boolean)
            .join(' ')
            .trim();
        return doctor.name || fullName || 'SIN MEDICO';
    };

    useEffect(() => {
        fetchCatalogStudies();
        fetchCategories();
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/staff-doctors`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const doctorsList = [
                    { id: 0, name: 'SIN MÉDICO' },
                    ...data
                ];
                setDoctors(doctorsList);

                // Try to auto-select if current user is a doctor
                const userId = localStorage.getItem('userId');
                if (userId) {
                    const loggedDoc = doctorsList.find(d => d.id.toString() === userId.toString());
                    if (loggedDoc) setRequestingDoctor(loggedDoc.id);
                }
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    // Bug 1 fix: debounce search to avoid excessive API calls on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCatalogStudies();
        }, 300);
        return () => clearTimeout(timer);
    }, [filters]);

    const fetchCatalogStudies = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const queryParams = new URLSearchParams();
            queryParams.append('tipo_item', 'estudio');
            if (filters.search) queryParams.append('search', filters.search);
            if (filters.category) queryParams.append('category', filters.category);
            if (filters.processing_type) queryParams.append('processing_type', filters.processing_type);

            const res = await fetch(`${API_URL}/catalog-studies?${queryParams}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCatalogStudies(data);
            }
        } catch (error) {
            console.error('Error fetching catalog:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/catalog-studies/categories`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSelectStudy = (study) => {
        setSelectedStudy(study);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) {
            return;
        }
        if (!requestingDoctor && requestingDoctor !== 0) {
            setError('Debe seleccionar el médico solicitante (o SIN MÉDICO)');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/patients/${patientId}/studies`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    catalog_study_id: selectedStudy.id,
                    requesting_doctor_id: requestingDoctor,
                    priority: formData.priority,
                    special_notes: formData.special_notes,
                    is_cost_exempt: formData.is_cost_exempt
                })
            });

            if (res.ok) {
                onSuccess();
                onClose();
            } else {
                const errorData = await res.json();
                setError(errorData.message || 'Error al asignar estudio');
            }
        } catch (error) {
            setError('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content assign-study-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h2>Asignar Estudio</h2>
                        <p className="patient-label">Paciente: {patientName}</p>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {/* Search and Filters */}
                        <div className="search-section">
                            <div className="search-box">
                                <input
                                    type="text"
                                    placeholder="Buscar estudio..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Study List */}
                        <div className="study-list-container">
                            <h4>Selecciona un estudio:</h4>
                            {loading ? (
                                <div className="loading-list">Cargando estudios...</div>
                            ) : catalogStudies.length === 0 ? (
                                <div className="empty-list">No se encontraron estudios</div>
                            ) : (
                                <div className="studies-grid">
                                    {catalogStudies.map(study => (
                                        <div
                                            key={study.id}
                                            className={`study-card ${selectedStudy?.id === study.id ? 'selected' : ''}`}
                                            onClick={() => handleSelectStudy(study)}
                                        >
                                            <div className="card-selection-marker">
                                                <div className="marker-dot" />
                                            </div>
                                            
                                            <div className="study-card-body">
                                                <div className="study-type-badge">
                                                    <Tag size={12} />
                                                    {study.category || 'General'}
                                                </div>
                                                
                                                <h4 className="study-title" title={study.name}>{study.name}</h4>
                                                
                                                <div className="study-card-footer">
                                                    <div className="study-price-tag">
                                                        <DollarSign size={14} />
                                                        {parseFloat(study.price || 0).toFixed(2)}
                                                    </div>
                                                    
                                                    <div className="card-meta-info">
                                                        <span className="meta-item" title="Tiempo estimado">
                                                            <Clock size={12} />
                                                            {study.estimated_time || '-'} d
                                                        </span>
                                                        <span className={`meta-item processing-type ${study.processing_type}`} title="Procesamiento">
                                                            <Activity size={12} />
                                                            {study.processing_type === 'externo' ? 'Ext.' : 'Int.'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Selected Study Instructions */}
                        {selectedStudy && selectedStudy.patient_instructions && (
                            <div className="instructions-section">
                                <h4>
                                    <AlertCircle size={16} />
                                    Indicaciones para el paciente
                                </h4>
                                <p>{selectedStudy.patient_instructions}</p>
                            </div>
                        )}

                        {/* Additional Fields */}
                        <div className="form-section">
                            <div className="form-group">
                                <label><Stethoscope size={16} /> Médico Solicitante</label>
                                <select
                                    className={`doctor-select ${(error && !requestingDoctor && requestingDoctor !== 0) ? 'input-error' : ''}`}
                                    value={requestingDoctor}
                                    onChange={(e) => setRequestingDoctor(e.target.value)}
                                    required
                                >
                                    <option value="" disabled>Seleccionar médico...</option>
                                    {doctors.map(doc => (
                                        <option key={doc.id} value={doc.id}>
                                            {getDoctorDisplayName(doc)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group full-width">
                                <label>Notas especiales (opcional)</label>
                                <textarea
                                    value={formData.special_notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, special_notes: e.target.value }))}
                                    placeholder="Ej: Paciente diabético, verificar glucosa antes..."
                                    rows={3}
                                />
                            </div>

                            <div className="form-group priority-group">
                                <label>Prioridad</label>
                                <div className="radio-options">
                                    <label className={`radio-label ${formData.priority === 'rutina' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="priority"
                                            value="rutina"
                                            checked={formData.priority === 'rutina'}
                                            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                                        />
                                        <span className="radio-indicator rutina" />
                                        Rutina
                                    </label>
                                    <label className={`radio-label ${formData.priority === 'urgente' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="priority"
                                            value="urgente"
                                            checked={formData.priority === 'urgente'}
                                            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                                        />
                                        <span className="radio-indicator urgente" />
                                        Urgente
                                    </label>
                                </div>
                            </div>

                            {/* Cost Exemption */}
                            <div className="form-group exempt-group full-width">
                                <div
                                    className={`exempt-label ${formData.is_cost_exempt ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, is_cost_exempt: !prev.is_cost_exempt }))}
                                >
                                    <div className={`exempt-checkbox ${formData.is_cost_exempt ? 'checked' : ''}`}>
                                        {formData.is_cost_exempt && <Gift size={14} />}
                                    </div>
                                    <div className="exempt-text">
                                        <span className="exempt-title">Exentar costo del estudio</span>
                                        <span className="exempt-desc">
                                            {formData.is_cost_exempt
                                                ? `Este estudio se cobrará en $0.00 (precio original: ${selectedStudy ? `$${parseFloat(selectedStudy.price || 0).toFixed(2)}` : '$0.00'})`
                                                : 'Marcar si el paciente no pagará por este estudio'
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="error-message">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={!selectedStudy || submitting}
                        >
                            {submitting ? 'Asignando...' : 'Asignar Estudio'}
                        </button>
                    </div>
                </form>
            </div >
        </div >
    );
};

export default AssignStudyModal;
